import { useQuery } from '@tanstack/react-query';
import {
  loadViewDataQueryFn,
  type ViewDataParams,
} from 'shared/components/CustomTable/hooks/loadViewData';
import {
  type ApiError,
  addResourceNameToQueryResult,
  useLegacyAxiosInstance,
} from 'shared/hooks/utils';

export type LoadUnhandledDebtorsViewParams = ViewDataParams & { view_id: string };

export type UnhandledDebtorsView = Awaited<ReturnType<typeof loadViewDataQueryFn>>;

// Per-resource wrapper over the generic views endpoint (loadViewDataQueryFn),
// mirroring useGetDebtorsView but scoped to the at-risk (unhandled_debtors)
// view type so its cache is independent from the /clients debtors list.
const emptyUnhandledDebtors: UnhandledDebtorsView = { data: [], metadata: undefined };

export function useLoadUnhandledDebtorsView(params: LoadUnhandledDebtorsViewParams) {
  const axiosInstance = useLegacyAxiosInstance();
  const { view_id, ...filters } = params;

  const queryResult = useQuery<UnhandledDebtorsView, ApiError>({
    queryKey: ['unhandled-debtors-view', params],
    queryFn: () => loadViewDataQueryFn(axiosInstance, view_id, filters),
    enabled: Boolean(view_id),
    placeholderData: previousData => previousData ?? emptyUnhandledDebtors,
  });

  return addResourceNameToQueryResult<
    UnhandledDebtorsView,
    ApiError,
    'unhandledDebtors',
    'PreventUndefined'
  >('unhandledDebtors', queryResult, emptyUnhandledDebtors);
}
